/**
 * Central Grade Color Definition
 * 
 * This file defines the authoritative source for grade ranges, colors, and labels
 * used throughout the application.
 */

export const GRADE_CONFIG = [
  { min: 5.75, max: 6.00, label: 'Excellent', color: '#166534' },
  { min: 5.25, max: 5.74, label: 'Very Good', color: '#15803d' },
  { min: 4.75, max: 5.24, label: 'Good', color: '#22c55e' },
  { min: 4.25, max: 4.74, label: 'Satisfactory', color: '#4ade80' },
  { min: 4.00, max: 4.24, label: 'Pass', color: '#f97316' },
  { min: 3.50, max: 3.99, label: 'Fail', color: '#ea580c' },
  { min: 3.00, max: 3.49, label: 'Poor', color: '#dc2626' },
  { min: 2.50, max: 2.99, label: 'Very Poor', color: '#b91c1c' },
  { min: 2.00, max: 2.49, label: 'Extremely Poor', color: '#991b1b' },
  { min: 1.00, max: 1.99, label: 'Not Measurable', color: '#7f1d1d' }
];

/**
 * Get the configuration object for a specific grade value
 * @param {number} gradeValue 
 * @returns {object|null} The grade configuration object or null if not found
 */
export const getGradeConfig = (gradeValue) => {
  const grade = parseFloat(gradeValue);
  if (isNaN(grade)) return null;
  
  // Handle edge case for exactly 6.0 or slightly above due to rounding
  if (grade > 6.0) return GRADE_CONFIG[0];

  return GRADE_CONFIG.find(config => grade >= config.min && grade <= config.max) || 
         // Fallback for grades that might fall into gaps (though ranges cover everything from 1.0 to 6.0)
         // or below 1.0
         (grade < 1.0 ? GRADE_CONFIG[GRADE_CONFIG.length - 1] : GRADE_CONFIG[0]);
};

/**
 * Get the color hex code for a grade
 * @param {number} gradeValue 
 * @returns {string} Hex color code
 */
export const getGradeColor = (gradeValue) => {
  const config = getGradeConfig(gradeValue);
  return config ? config.color : '#9ca3af'; // Default gray for invalid/unknown
};

/**
 * Get the label for a grade
 * @param {number} gradeValue 
 * @returns {string} Grade label
 */
export const getGradeLabel = (gradeValue) => {
  const config = getGradeConfig(gradeValue);
  return config ? config.label : 'N/A';
};

/**
 * Get Tailwind CSS classes for a grade (Legacy support / Utility)
 * This maps the hex colors to approximate Tailwind classes or uses inline styles if needed.
 * Ideally, components should use the hex color directly via style prop or custom CSS variables.
 * 
 * @param {number} gradeValue 
 * @returns {object} Object containing text and bg classes/styles
 */
export const getGradeStyles = (gradeValue) => {
  const color = getGradeColor(gradeValue);
  return {
    color: color,
    borderColor: color,
    backgroundColor: `${color}20` // 20 is hex for ~12% opacity
  };
};

// Keep existing function signature for backward compatibility but update logic
export const getGradeColorClasses = (gradeValue) => {
  const config = getGradeConfig(gradeValue);
  
  if (!config) {
    return {
      text: 'text-gray-400',
      bg: 'bg-gray-100',
      badge: 'bg-gray-100 text-gray-600',
      border: 'border-gray-200',
      progress: 'bg-gray-400'
    };
  }

  // We return inline styles for color-specific properties where possible, 
  // but since this function returns class strings, we might need to rely on the caller using style props
  // or we can try to map to nearest tailwind colors if we really wanted to, but the requirement is specific hex codes.
  // So we will return generic classes + a style object or just assume the caller will use the hex.
  // However, existing callers expect classes. 
  // To strictly follow "No hardcoded grade colors", we should update callers to use `getGradeColor` and `style={{ color: ... }}`.
  // But to avoid breaking everything immediately, I will map to the closest existing classes OR 
  // better yet, I will return empty color classes and expect the caller to use the style prop if I can update them.
  
  // Actually, let's look at how `getGradeColorClasses` is used.
  // It returns objects like { text: '...', bg: '...' }.
  // If I return `text-[${config.color}]` it requires JIT which might be enabled.
  // Let's assume JIT is enabled or I should update the components.
  
  return {
    text: `text-[${config.color}]`, 
    bg: `bg-[${config.color}]`,
    badge: `bg-[${config.color}] text-white`,
    border: `border-[${config.color}]`,
    progress: `bg-[${config.color}]`,
    hex: config.color 
  };
};

export const getGradeDescription = getGradeLabel;

/**
 * Get grade icon
 * @param {number|string} gradeValue - The grade value (1-6)
 * @returns {string} FontAwesome icon class
 */
export const getGradeIcon = (gradeValue) => {
  const grade = parseFloat(gradeValue);
  
  if (isNaN(grade)) return 'fas fa-question';
  
  if (grade >= 5.75) return 'fas fa-star';
  if (grade >= 5.25) return 'fas fa-thumbs-up';
  if (grade >= 4.75) return 'fas fa-check';
  if (grade >= 4.25) return 'fas fa-check-circle';
  if (grade >= 4.00) return 'fas fa-minus';
  if (grade >= 3.50) return 'fas fa-exclamation-triangle';
  return 'fas fa-times';
};

/**
 * Calculate percentage for progress bars
 * @param {number|string} gradeValue - The grade value (1-6)
 * @returns {number} Percentage (0-100)
 */
export const getGradePercentage = (gradeValue) => {
  const grade = parseFloat(gradeValue);
  if (isNaN(grade)) return 0;
  return Math.min(Math.max((grade / 6) * 100, 0), 100);
};

/**
 * Grade Legend Component Data
 */
export const gradeScheme = GRADE_CONFIG.map(config => ({
  range: `${config.min.toFixed(2)}-${config.max.toFixed(2)}`,
  description: config.label,
  color: `bg-[${config.color}] text-white`,
  icon: getGradeIcon((config.min + config.max) / 2)
}));

/**
 * Format grade value for display
 * @param {number|string} gradeValue - The grade value
 * @param {number} decimals - Number of decimal places (default: 1)
 * @returns {string} Formatted grade string
 */
export const formatGrade = (gradeValue, decimals = 1) => {
  const grade = parseFloat(gradeValue);
  if (isNaN(grade)) return '—';
  return grade.toFixed(decimals);
};
